import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';
import FullPaid from "../../../images/full_paid.svg";
import HalfPaid from "../../../images/half_checked.svg";
import ScheduledToPay from "../../../images/managers/scheduled_to_pay.png";
import TenantRecord from '../../../images/tenant_claim_paid.svg';
import FailedPayment from "../../../images/managers/redCircle.png";
import {BllinkLogger} from "../../../common/utils/bllink_loggers";
import { i18n } from '../../../common/translations';
import BllinkUtils from "../../../common/utils/utils";
import { constants } from "../../../common/constants/constants";
import { unique } from "react-table/src/aggregations";
import cloneDeep from "lodash.clonedeep";

export default class MonthTableLogic {
    static renderIcon(monthData, options, tenantName) {
        if (!monthData) {
            return <div/>;
        }
        const data =  cloneDeep(monthData); // NOTE: monthData is immutable
        const result = this.monthRelatedIconData(data, options);
        result.toolTipText = this.addPaymentMethodsTextIfNeeded(result.toolTipText, data);

        if (result.src && tenantName !== i18n.t('reports.percentage')) {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={props => (
                        <Tooltip {...props}>
                            {ReactHtmlParser(result.toolTipText)}
                        </Tooltip>
                    )}
                >
                    <div exceltext={result.excelText}>
                        <img className="paid-circle center" alt={result.alt} src={result.src}/>
                        <span className="month-label"> {BllinkUtils.Strings.numberWithCommas(result.label)} </span>
                    </div>
                </OverlayTrigger>
            );
        }
        if (tenantName === i18n.t('reports.percentage')) {
            const percNum = +result.label.slice(0, -1);
            let color = '';
            if (percNum < 30) color = 'red-text'
            else if (percNum >= 30 && percNum < 70) color = 'orange-text'
            else if (percNum >= 70 && percNum < 90) color = 'light-green-text'
            else if (percNum >= 90) color = 'green-text'

            return (
                <div exceltext={result.excelText}>
                    <span className={`month-label ${color}`}> {BllinkUtils.Strings.numberWithCommas(result.label)} </span>
                </div>
            )
        }

        if (result.renderEmpty) {
            return '';
        }

        BllinkLogger.error(`un handled case for month data`, monthData);
        throw new Error(`un handled case for month data`);
    }

    static monthRelatedIconData(monthData, options) {

        // when we switch years. react keeps the tooltip icons for some reason..
        let alt, src, label = monthData.total.value, toolTipText, excelText = '', renderEmpty = false;
        let fullOrOverPaid = monthData.total.value <= monthData.paid.value;
        const didntPay = monthData.paid.value === 0 && monthData.scheduled_to_pay.value === 0;
        const partlyPaid = monthData.paid.value < monthData.total.value && monthData.paid.value > 0;
        let scheduledToPay = monthData.scheduled_to_pay.value > 0;
        const failedPayment = monthData.isFailed;
        const isFutureCharge = monthData.isFutureCharge;

        monthData.payment_methods = unique(monthData.payment_methods);
        const isTenantRecord = monthData.payment_methods?.includes(constants.managerPaymentsOptions.tenantRecordKey)
            && monthData?.is_tenant_record_not_approved;
        fullOrOverPaid = fullOrOverPaid && !isFutureCharge;
        scheduledToPay = scheduledToPay || isFutureCharge;
        if (isFutureCharge){
            monthData.scheduled_to_pay.value = monthData.paid.value;
        }
        //https://app.asana.com/0/1196545425570329/1199534726301541
        // add currency to excel sheets
        // https://app.asana.com/0/1196545425570329/1199892040071070
        // removing currency because It can't be summed, since it's a string
        // const currency = i18n.t('currency.' + monthData.total.currency.toLowerCase());

        if (failedPayment) {
            alt = 'Failed To Pay';
            src = FailedPayment;
            toolTipText = i18n.t('reports.failed_payment');
            excelText = monthData.total.value
        } else if (isTenantRecord) {
            alt = 'Tenant Claims Payed';
            src = TenantRecord;
            label = monthData.paid.value;
            toolTipText = i18n.t('reports.tenant_claim_paid_explanation', {paid: monthData.paid.value});
            excelText = monthData.paid.value;
        } else if (fullOrOverPaid) {
            alt = 'Full Paid';
            src = FullPaid;
            label = monthData.paid.value;
            toolTipText = i18n.t('reports.paid_full', {paid: monthData.paid.value});
            excelText = monthData.paid.value;
        } else if (didntPay) {
            // https://app.asana.com/0/1196545425570329/1199562987978280
            // Monthly report: keep row blank if when there's no payment
            //
            // alt = 'None Paid';
            // src = NonePaid;
            // toolTipText = i18n.t('reports.paid_none');
            // excelText = `0`;
            renderEmpty = true;
        } else if (partlyPaid) {
            alt = 'Half Paid';
            src = HalfPaid;

            toolTipText = this.getPartPayTooltipText(monthData, options.tableType);
            excelText = `${monthData.paid.value} / ${monthData.total.value}`;
        } else if (scheduledToPay) {
            alt = 'Scheduled To Pay';
            src = ScheduledToPay;
            toolTipText = i18n.t('reports.scheduled_to_pay', {scheduledToPay: monthData.scheduled_to_pay.value, total: monthData.total.value});
            excelText = monthData.scheduled_to_pay.value
        }

        return {
            alt: alt,
            src: src,
            label: label,
            toolTipText: toolTipText,
            excelText: excelText,
            renderEmpty: renderEmpty,
        };
    }

    static addPaymentMethodsTextIfNeeded(tooltipText, monthData) {
        //https://app.asana.com/0/1196545425570329/1199593487650813
        //Add tooltip to show payment method

        if (monthData.payment_methods) {
            monthData.payment_methods = unique(monthData.payment_methods);
            // BllinkLogger.info(`monthData with method`, monthData);
            tooltipText = tooltipText || '';
            tooltipText += '<br/>';
            let isFirst = true;
            if (monthData.payment_methods && monthData.payment_methods.length > 0) {
                tooltipText += i18n.t('reports.paid_with') + ' ';
            }
            for (const paymentMethod of monthData.payment_methods) {
                if (constants.managerPaymentsOptions.isValidMethod(paymentMethod)) {
                    if (isFirst) {
                        tooltipText += i18n.t(`buildings.${paymentMethod}`);
                        isFirst = false
                    } else {
                        tooltipText += '+ ' + i18n.t(`buildings.${paymentMethod}`);
                    }
                } else {
                    BllinkLogger.error(`invalid method`, paymentMethod)
                }
            }
        }

        return tooltipText;
    }



    static getPartPayTooltipText(monthData, tableType) {
        let toolTipText;
        let totalDebt = BllinkUtils.Money.roundNumber(monthData.total.value - monthData.paid.value);
        if (tableType  === 'yearlyByMonths') {
            toolTipText = i18n.t('reports.total_debt', {debt: totalDebt});
            toolTipText += ` <br/> ` + i18n.t('reports.paid_out_of_total', {
                paid: monthData.paid.value,
                total: monthData.total.value
            });

        } else {
            // https://app.asana.com/0/1196545425570329/1199553132434526
            // Cash flow: change tooltip part pay for yearly cashflow only
            toolTipText = i18n.t('reports.paid', {paid: monthData.paid.value});
            toolTipText += ` <br/> ` + i18n.t('reports.scheduled_to_pay_only', {scheduledToPay: monthData.scheduled_to_pay.value});

        }

        return toolTipText;
    }

    static renderMonthText(monthData, options) {
        return monthData;
    }
}
